import { useMutation } from '@apollo/client';
import { createForm } from '@seek/forms-ui';
import {
  Box,
  Card,
  ContentBlock,
  Heading,
  IconShare,
  List,
  Stack,
  Strong,
  Text,
  TextLink,
  useToast,
} from 'braid-design-system';
import React, { useRef, useState } from 'react';
import Youtube from 'react-youtube';

import {
  MarketplaceInsightsSignupDocument,
  type MarketplaceInsightsSignupInput,
  type UserRegistrationType,
} from 'src/generated/graphql';

import { SalesCollateralCta } from './components/SalesCollateralCta';
import { SignupForm } from './components/SignupForm';
import { Success } from './components/Success';
import CoverImage from './resources/2025-MarketplaceTrends.png';
import { shareInsightsReportEmailHref } from './resources/shareInsightsReportEmailLink';

import * as styles from './MarketplaceInsights.css';

export interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  userType: UserRegistrationType | undefined;
  checkbox: boolean;
}

export const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  userType: undefined,
  checkbox: false,
};

export const { FormProvider, useField } = createForm<FormValues>(
  'marketplaceInsightsReportSignup',
);

export const MarketplaceInsights = () => {
  const [signup, { loading }] = useMutation(MarketplaceInsightsSignupDocument);
  const [formSubmitted, setFormSubmitted] = useState<
    | { kind: 'idle' }
    | { kind: 'submitted'; userRegistrationType: UserRegistrationType }
  >({ kind: 'idle' });
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const showToast = useToast();

  const onSubmit = async (formData: FormValues) => {
    if (formData.checkbox === false || formData.userType === undefined) {
      return;
    }

    const signupInput: MarketplaceInsightsSignupInput = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      emailAddress: formData.email,
      userRegistrationType: formData.userType,
    };

    try {
      await signup({ variables: { input: signupInput } });
    } catch {
      showToast({
        tone: 'critical',
        message: 'There was an unexpected issue, please contact our support',
      });
      return {};
    }

    contentContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    setFormSubmitted({
      kind: 'submitted',
      userRegistrationType: formData.userType,
    });
    return {};
  };

  return (
    <ContentBlock>
      <Box paddingY="large" ref={contentContainerRef}>
        <Card>
          <Stack space={{ mobile: 'small', tablet: 'large' }}>
            <Box display={{ mobile: 'block', tablet: 'flex' }}>
              <Box marginRight={{ mobile: 'none', tablet: 'xlarge' }}>
                {formSubmitted.kind === 'submitted' ? (
                  <Success />
                ) : (
                  <Stack space="gutter">
                    <Box display="flex" justifyContent="flexEnd">
                      <TextLink
                        hitArea="large"
                        href={shareInsightsReportEmailHref}
                      >
                        <Text>
                          Share <IconShare />
                        </Text>
                      </TextLink>
                    </Box>

                    <Text tone="secondary">EXCLUSIVE MARKET INSIGHTS</Text>
                    <Heading level="2" weight="weak">
                      Unlock Your Competitive Edge
                    </Heading>

                    <Youtube
                      videoId="iHOyttMvpm4"
                      opts={{ height: '250px', width: '100%' }}
                      className={styles.iframe}
                    />

                    <Box paddingY="medium">
                      <Stack space="gutter">
                        <Text tone="secondary">
                          Get ahead of the curve with the{' '}
                          <Strong>
                            SEEK Business Marketplace Insights Report
                          </Strong>{' '}
                          — your go-to resource for key trends and expert
                          analysis in the business-for-sale market.
                        </Text>
                        <List>
                          <Text tone="secondary">
                            <Strong>Key Market Trends</Strong> — State by state,
                            industry and price demand trends
                          </Text>
                          <Text tone="secondary">
                            <Strong>Expert Analysis</Strong> — Economic analysis
                            from SEEK&apos;s Senior Economist
                          </Text>
                          <Text tone="secondary">
                            <Strong>Actionable Insights</Strong> — Predictions
                            to help you plan ahead
                          </Text>
                        </List>
                        <Text tone="secondary">
                          <Strong>
                            Sign up now and stay ahead of the competition!
                          </Strong>
                        </Text>
                      </Stack>
                    </Box>

                    <FormProvider initialValues={initialValues}>
                      {(props) => (
                        <SignupForm
                          {...props}
                          onSubmit={onSubmit}
                          loading={loading}
                        />
                      )}
                    </FormProvider>
                  </Stack>
                )}
              </Box>

              <Box
                display={{
                  mobile: 'none',
                  tablet: 'block',
                }}
                style={{ minHeight: 966, minWidth: 400, width: '100%' }}
              >
                <img
                  src={CoverImage}
                  className={styles.coverImage}
                  alt="Seek Business marketplace insights enquiry trends"
                />
              </Box>
            </Box>

            {formSubmitted.kind === 'submitted' ? (
              <SalesCollateralCta
                userRegistrationType={formSubmitted.userRegistrationType}
              />
            ) : null}
          </Stack>
        </Card>
      </Box>
    </ContentBlock>
  );
};
